import React, { ReactElement } from 'react';
import { Droppable } from 'react-beautiful-dnd';
import { Wrapper } from '@screentone/core';
import { FetchNextPageOptions, InfiniteQueryObserverResult } from '@tanstack/react-query';

import { ResponsiveLoader } from 'components';
import { InfiniteScrollWaypoint } from 'components/infinite-scroll-waypoint/InfiniteScrollWaypoint';
import { NoSearchResults } from 'components/no-search-results/NoSearchResults';
import { AllessehContent, AllessehContentQueryResponse } from 'hooks/useAllessehContentQuery';
import { DROPPABLE_IDS } from 'utils/collectionDragUtils';
import { AlreadyInScratchpadMessage } from './AlreadyInScratchpadMessage';
import styles from './SearchContentList.module.scss';

type RenderContentCardFunction = (content: AllessehContent, index: number, embargo?: boolean) => ReactElement;

interface InnerListProps {
  includedContentItemsInSearch: AllessehContent[];
  renderContentCard: RenderContentCardFunction;
  embargo?: boolean;
}

const InnerList = React.memo(({ includedContentItemsInSearch, renderContentCard, embargo }: InnerListProps) => (
  <>
    {includedContentItemsInSearch.map((content, index: number) => (
      <div data-testid="search-content-list-item" key={content.data.id}>
        {renderContentCard(content, index, embargo)}
      </div>
    ))}
  </>
));

InnerList.displayName = 'SearchContentInnerList';

interface ContentsListProps {
  includedContentItemsInSearch: AllessehContent[];
  excludedContentItemsInSearch: AllessehContent[];
  className?: string;
  renderContentCard: RenderContentCardFunction;
  nextPageProps?: {
    fetchNextPage: (
      options?: FetchNextPageOptions | undefined
    ) => Promise<InfiniteQueryObserverResult<AllessehContentQueryResponse>>;
    isFetchingNextPage: boolean;
    hasNextPage?: boolean;
  };
  isLoading: boolean;
  customMessage?: string;
  isContentTypeFilterEmpty?: boolean;
  embargo?: boolean;
  scratchpadItemIds?: string[];
}

const SearchContentList = ({
  includedContentItemsInSearch,
  excludedContentItemsInSearch,
  className,
  nextPageProps,
  isLoading,
  customMessage,
  isContentTypeFilterEmpty,
  renderContentCard,
  embargo,
  scratchpadItemIds
}: ContentsListProps) => (
  <div
    data-testid="search-content-list-container"
    className={`${className ? `${className} ` : ''}${isContentTypeFilterEmpty ? '' : styles.container}`}
  >
    <Wrapper data-testid="search-content-list-container-list" margin={{ top: 'sm' }}>
      {isLoading && <ResponsiveLoader />}
      {!isLoading &&
        (!(includedContentItemsInSearch[0] || excludedContentItemsInSearch[0]) ||
        includedContentItemsInSearch[0]?.response?.error ||
        (embargo && !includedContentItemsInSearch[0]) ? (
          <NoSearchResults
            data-testid="search-content-list-no-results"
            title="Content"
            bodyText={
              customMessage ??
              (isContentTypeFilterEmpty ? 'Please select a filter to show results' : 'No matching content found')
            }
          />
        ) : (
          <>
            <Droppable droppableId={DROPPABLE_IDS.RESULTS} isDropDisabled>
              {(droppableProvided) => (
                <div
                  data-testid="search-content-list-content"
                  ref={droppableProvided.innerRef}
                  {...droppableProvided.droppableProps}
                  className={styles.itemsList}
                >
                  <InnerList
                    includedContentItemsInSearch={includedContentItemsInSearch}
                    renderContentCard={renderContentCard}
                    embargo={embargo}
                  />
                  {droppableProvided.placeholder}
                </div>
              )}
            </Droppable>
            {!embargo &&
              !includedContentItemsInSearch[0] &&
              excludedContentItemsInSearch.map((content) =>
                scratchpadItemIds?.includes(content.data.id) ? (
                  <AlreadyInScratchpadMessage key={content.data.id} />
                ) : (
                  <NoSearchResults
                    data-testid="search-content-dedupe-message"
                    title="Content"
                    bodyText="No content found. The item you are looking for may already be curated on this page."
                    key={content.data.id}
                  />
                )
              )}
            {nextPageProps?.fetchNextPage && <InfiniteScrollWaypoint nextPageProps={nextPageProps} />}
          </>
        ))}
    </Wrapper>
  </div>
);

export default SearchContentList;
